import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import FooterSection from '../../components/FooterSection';
import { WA_NUMBER } from '../../constant/detail';

const relatedServices = [
  { label: 'Jasa Maintenance Kolam Renang Bali', to: '/jasa-maintenance-kolam-renang-bali' },
  { label: 'Jual Chemical Pool Bali', to: '/jual-chemical-pool-bali' },
  { label: 'Pasang Filter Kolam Renang Bali', to: '/pasang-filter-kolam-renang-bali' },
  { label: 'Jasa Testkit Air Kolam Bali', to: '/jasa-testkit-air-kolam-bali' },
  { label: 'Jual Equipment Pool Bali', to: '/equipment-pool-bali' },
];

const WA_MESSAGE = encodeURIComponent(
  'Hello, I would like to get expert advice about my pool in Bali'
);

const ExpertAdvice = () => {
  return (
    <>
      <Head>
        <title>Konsultasi Kolam Renang Bali | Expert Pool Advice | SJT</title>
        <meta
          name="description"
          content="Konsultasi kolam renang Bali dari spesialis berpengalaman. Panduan teknis, rekomendasi chemical, desain & tips perawatan. Konsultasi pertama gratis. Hubungi kami!"
        />
        <link rel="canonical" href="https://poolshopsuryajayateknik.com/konsultasi-kolam-renang-bali" />
        <meta property="og:title" content="Konsultasi Kolam Renang Bali | Expert Pool Advice | SJT" />
        <meta property="og:description" content="Konsultasi kolam renang Bali dari spesialis berpengalaman. Panduan teknis, rekomendasi chemical, desain & tips perawatan. Konsultasi pertama gratis." />
        <meta property="og:image" content="https://poolshopsuryajayateknik.com/img/blog/img-2.jpg" />
        <meta property="og:url" content="https://poolshopsuryajayateknik.com/konsultasi-kolam-renang-bali" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Konsultasi Kolam Renang Bali | Expert Pool Advice | SJT" />
        <meta name="twitter:description" content="Konsultasi kolam renang Bali dari spesialis berpengalaman. Panduan teknis, rekomendasi chemical, desain & tips perawatan. Konsultasi pertama gratis." />
      </Head>

      <main className="mt-16 bg-white text-gray-800">
        {/* Hero */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              Pool Expert Advice Bali – Professional Guidance from Specialists
            </h1>
            <p className="text-lg text-primary/80 mb-8">
              Have questions about your pool? From water quality issues and
              equipment selection to filtration system design and new build cost
              estimates, the expert team at Suryajaya Teknik is ready to give
              you accurate and reliable technical advice. With over a decade of
              experience handling hundreds of pool projects across Bali, we have
              the deep knowledge to help you make the right decisions. Your
              first consultation is completely free because we believe that a
              well-informed customer is a satisfied customer.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-8 rounded-full text-lg transition"
            >
              Get Consultation Now
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Why Consulting a Pool Expert Matters
          </h2>
          <p className="mb-4 leading-relaxed">
            Many pool problems can actually be prevented if owners get the right
            advice from the start, both during the planning and operational
            stages. Mistakes in pump sizing, filter capacity, or chemical dosage
            can lead to financial losses far greater than the cost of
            professional consultation. In Bali with its unique climate and high
            hygiene standards for the tourism sector, wrong technical decisions
            can impact your business reputation. Our consultant team does not
            just give theoretical advice - we give practical guidance based on
            real field experience over many years.
          </p>
          <p className="leading-relaxed">
            We offer consultation services that can be conducted in person at
            your pool location or online via WhatsApp or video call. For on-site
            consultations, our team will do a thorough inspection of the current
            pool condition and systems before providing recommendations. Every
            piece of advice we give is based on your pool's actual condition,
            not a generic solution that may not fit your specific needs. We also
            provide a written consultation report as documentation and an action
            guide you can keep and refer to at any time.
          </p>
        </section>

        {/* Topics */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">
              Topics We Can Help You With
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                {
                  name: 'Water Quality Issues',
                  desc: 'Cloudy, green, smelly, or irritating water. We help diagnose the cause and find the right solution.',
                },
                {
                  name: 'Chemical Selection',
                  desc: 'The right type and dosage of chemicals based on pool volume, usage frequency, and current water condition.',
                },
                {
                  name: 'Filtration System',
                  desc: 'Recommendations on the optimal filter type and capacity suited to your pool size and usage.',
                },
                {
                  name: 'Equipment Selection',
                  desc: 'A guide to choosing the best pump, filter, and pool accessories for your budget and specific needs.',
                },
                {
                  name: 'Pool Renovation',
                  desc: 'Technical advice for renovation, system upgrades, or structural repairs on an existing pool.',
                },
                {
                  name: 'New Pool Construction',
                  desc: 'Initial consultation for planning a new pool, including cost estimates and technical specification recommendations.',
                },
              ].map((topik, i) => (
                <div
                  key={i}
                  className="bg-white border border-primary rounded-xl p-5 shadow-sm"
                >
                  <h3 className="font-semibold text-tertiary mb-2">
                    {topik.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{topik.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Why Consult with Us?
          </h2>
          <ul className="space-y-3">
            {[
              'Over 10 years of experience handling hundreds of pools across Bali',
              'First consultation just by WA with no obligation to use our services',
              'Advice based on actual conditions, not generic solutions',
              'Available online (WhatsApp/video call) or in-person visits',
              'Written consultation report as a saved action guide',
              'Brand-neutral - we recommend the best solution, not the most expensive',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-tertiary font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* CTA */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Start Your Consultation Now
            </h2>
            <p className="text-primary/80 mb-8">
              Do not let your pool problems drag on. Contact our expert team via
              WhatsApp now and get professional advice that is accurate, fast,
              and WA for your first consultation. We are ready to help you find
              the best solution.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-10 rounded-full text-lg transition"
            >
              Chat WhatsApp Now
            </a>
          </div>
        </section>

        {/* Related Services */}
        <section className="max-w-4xl mx-auto py-10 px-6">
          <h2 className="text-lg font-bold text-tertiary mb-4">Layanan Lainnya</h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link key={to} to={to} className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <FooterSection />
      </main>
    </>
  );
};

export default ExpertAdvice;
